import { getByPath, matchValue } from "discourse/lib/blocks";
import {
  BlockCondition,
  type ConditionContext,
  type ConditionResolvedValue,
} from "./condition";
import { blockCondition } from "./decorator";

/**
 * Maximum allowed length for outlet arg paths.
 * Prevents potential memory and performance issues from extremely long paths.
 */
const MAX_PATH_LENGTH = 255;

/** Args accepted by the `outlet-arg` condition. */
interface OutletArgConditionArgs {
  /** Dot-notation path to the property, e.g. `"topic.closed"`,
   *  `"user.trust_level"`, `"category.id"`. */
  path: string;

  /** Value to match against (see matching rules below). */
  value?: unknown;

  /** If true, passes when the property exists (not undefined); if false,
   *  passes when the property is undefined. */
  exists?: boolean;
}

/**
 * A condition that evaluates based on outlet arg values.
 *
 * Checks properties passed via `@outletArgs` on the BlockOutlet. Supports
 * dot-notation paths for nested properties and flexible value matching.
 *
 * ## Value Matching Rules
 *
 * Uses the shared `matchValue` utility:
 *
 * - **Primitive**: Passes if `actual === value` (strict equality)
 * - **RegExp**: Passes if `actual` (coerced to string) matches the pattern
 * - **[...values]**: Passes if `actual` matches ANY element (OR logic)
 * - **`{ not: value }`**: Passes if `actual` does NOT match `value`
 * - **`{ any: [...] }`**: Passes if `actual` matches ANY spec in array (OR logic)
 *
 * @example
 * ```
 * // Check if topic is closed
 * { type: "outlet-arg", path: "topic.closed", value: true }
 * ```
 *
 * @example
 * ```
 * // Check user trust level is 2 or higher
 * { type: "outlet-arg", path: "user.trust_level", value: [2, 3, 4] }
 * ```
 *
 * @example
 * ```
 * // Check category is one of several IDs
 * { type: "outlet-arg", path: "category.id", value: [1, 2, 3] }
 * ```
 *
 * @example
 * ```
 * // Check if topic property exists
 * { type: "outlet-arg", path: "topic", exists: true }
 * ```
 *
 * @example
 * ```
 * // Check topic is NOT closed
 * { type: "outlet-arg", path: "topic.closed", value: { not: true } }
 * ```
 *
 * @example
 * ```
 * // Check category slug matches pattern
 * { type: "outlet-arg", path: "category.slug", value: /^support/ }
 * ```
 *
 * @example
 * ```
 * // Check topic is closed OR archived (using any)
 * { type: "outlet-arg", path: "topic.closed", value: { any: [true, { not: false }] } }
 * ```
 */
@blockCondition({
  type: "outlet-arg",
  args: {
    path: { type: "string", required: true },
    value: { type: "any" },
    exists: { type: "boolean" },
  },
  constraints: {
    exactlyOne: ["value", "exists"],
  },
  validate(args) {
    // The `args` schema declares `path` as a required string, validated
    // before this custom validator runs.
    const { path: argPath } = args as unknown as OutletArgConditionArgs;

    // Check length first to prevent regex DoS with extremely long strings.
    if (argPath.length > MAX_PATH_LENGTH) {
      return (
        `\`path\` exceeds maximum length of ${MAX_PATH_LENGTH} characters. ` +
        `Path length: ${argPath.length}.`
      );
    }

    // Validate path format (alphanumeric, underscores, dots)
    if (!/^[\w.]+$/.test(argPath)) {
      return (
        `\`path\` "${argPath}" is invalid. ` +
        `Use dot-notation with alphanumeric characters (e.g., "user.trust_level").`
      );
    }

    return null;
  },
})
export default class BlockOutletArgCondition extends BlockCondition {
  /**
   * Evaluates whether the outlet arg condition passes.
   */
  evaluate(args: Record<string, unknown>, context?: ConditionContext): boolean {
    const { path, value, exists } = args as unknown as OutletArgConditionArgs;
    const outletArgs = context?.outletArgs;

    // Get the value at the path
    const targetValue = getByPath(outletArgs, path);

    // Check existence if specified
    if (exists !== undefined) {
      const doesExist = targetValue !== undefined;
      return exists ? doesExist : !doesExist;
    }

    // The exactlyOne constraint ensures value or exists is always specified
    return matchValue({ actual: targetValue, expected: value });
  }

  /**
   * Returns the resolved value at the path for debug logging.
   */
  getResolvedValueForLogging(
    args: Record<string, unknown>,
    context?: ConditionContext
  ): ConditionResolvedValue {
    const { path, value, exists } = args as unknown as OutletArgConditionArgs;
    return {
      hasValue: true,
      formatted: {
        path,
        actual: getByPath(context?.outletArgs, path),
        configured: exists !== undefined ? { exists } : value,
      },
    };
  }
}
